import reactStringReplace from 'react-string-replace'
import styled from 'styled-components'

import { ParcelOutline, TypeStyle, fromMd } from '@syconium/little-miss-figgy'

import { ProductCatalogVariantGroup } from '../../../../../__generated__/graphql/catalog/graphql'
import { useFeatureFlag } from '../../../../../app/_providers/ExperimentationProvider.client'
import { useLocalization } from '../../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { IntlShippingAndTaxMsg } from '../../../../../brunswick/components/IntlShippingAndTaxMsg'

import * as SharedStyles from './styles'

type ProductDetailsPromoMessagesProps = {
  variantGroup: Pick<ProductCatalogVariantGroup, 'siteWidePromoEligible' | 'finalSale'>
}

export const ProductDetailsPromoMessages = ({ variantGroup }: ProductDetailsPromoMessagesProps) => {
  const { isUSRegion } = useLocalization()
  const {
    detailsSection: {
      freeShippingMessage,
      freeShippingMessageBold,
      freeShippingAndReturnsMessage,
      freeShippingAndReturnsMessageBold,
    },
    pages: { product: productPageStrings },
  } = useTranslation()

  const freeReturnsMessagingDecision = useFeatureFlag<'control' | 'icon-bold' | 'background-color'>(
    {
      key: 'free-returns-messaging',
      controlVariant: 'control',
      ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
      skip: !isUSRegion,
    }
  )

  // Parse strings and replace <strong> tags with BoldText component
  const parseBoldMarkup = (text: string) =>
    reactStringReplace(text, /<strong>(.*?)<\/strong>/g, (match, i) => (
      <SharedStyles.BoldText key={i}>{match}</SharedStyles.BoldText>
    ))

  const experimentShippingText =
    freeReturnsMessagingDecision !== 'control'
      ? parseBoldMarkup(freeShippingMessageBold)
      : freeShippingMessage

  const experimentFreeShippingAndReturnsText =
    freeReturnsMessagingDecision !== 'control'
      ? parseBoldMarkup(freeShippingAndReturnsMessageBold)
      : freeShippingAndReturnsMessage

  return (
    <>
      {/* Display shipping/tax information for the current locale */}
      <IntlMsgWrap>
        <IntlShippingAndTaxMsg />
      </IntlMsgWrap>

      {/* Promo exclusion messaging */}
      {!variantGroup.siteWidePromoEligible && (
        <PromoExclusionMessage>{productPageStrings.notEligibleForDiscount}</PromoExclusionMessage>
      )}

      {/* Free shipping messaging for domestic customers */}
      {isUSRegion && (
        <SharedStyles.ProductDetailsCopy style={{ margin: '24px 0', textAlign: 'center' }}>
          <ProductDetailsTitle
            $hasBackgroundColor={freeReturnsMessagingDecision === 'background-color'}
            $hasIcon={freeReturnsMessagingDecision !== 'control'}
            isUppercase={freeReturnsMessagingDecision === 'control'}
            textTransform={freeReturnsMessagingDecision === 'control' ? 'uppercase' : undefined}
          >
            <span>
              {variantGroup.finalSale
                ? experimentShippingText
                : experimentFreeShippingAndReturnsText}
            </span>
            {freeReturnsMessagingDecision !== 'control' && <ParcelOutline height={20} width={20} />}
          </ProductDetailsTitle>
        </SharedStyles.ProductDetailsCopy>
      )}
    </>
  )
}

export const ProductDetailsTitle = styled(TypeStyle.BodyNano)<{
  $hasBackgroundColor?: boolean
  $hasIcon?: boolean
}>`
  font-weight: 600;
  text-wrap: pretty;
  ${o =>
    o.$hasIcon &&
    `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${o.theme.spacing(2)};
  `}
  ${o =>
    o.$hasBackgroundColor &&
    `
    background-color: ${o.theme.color.fill.subtle};
    padding: ${o.theme.spacing(2)};
    border-radius: ${o.theme.spacing(1)};
  `}
`

export const IntlMsgWrap = styled(ProductDetailsTitle)`
  text-align: center;
  margin: ${o => o.theme.spacing(6)};

  ${fromMd} {
    margin: ${o => o.theme.spacing(4)} 0;
  }
`

export const PromoExclusionMessage = styled(TypeStyle.BodySmall)`
  color: ${o => o.theme.color.deprecated.obsidianLighter};
  text-align: center;
  font-weight: 400;
  font-size: 12px;
  margin: ${o => o.theme.spacing(6)};

  ${fromMd} {
    margin: ${o => o.theme.spacing(6)} 0;
  }
`
